import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { apiFetch } from '../lib/api.ts';
import { dateOnly, money, num } from '../lib/format.ts';

interface Row {
  salesman: string;
  invoiceCount: number;
  invoicedRevenue: number;
  uniqueCustomers: number;
  arOutstanding: number;
  openSoCount: number;
  openSoValue: number;
}

interface PerfResponse {
  range: { from: string; to: string };
  rows: Row[];
}

type PresetKey = '30d' | 'last-month' | 'last-quarter' | 'ytd' | 'last-year' | 'custom';

const PRESETS: Record<PresetKey, string> = {
  '30d': 'Last 30 days',
  'last-month': 'Last month',
  'last-quarter': 'Last quarter',
  'ytd': 'Year to date',
  'last-year': 'Last year',
  'custom': 'Custom',
};

function ymd(d: Date): string {
  return new Date(d).toISOString().slice(0, 10);
}

function presetRange(p: Exclude<PresetKey, 'custom'>): { from: string; to: string } {
  const today = new Date();
  switch (p) {
    case '30d': {
      const from = new Date(today);
      from.setDate(from.getDate() - 29);
      return { from: ymd(from), to: ymd(today) };
    }
    case 'last-month': {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: ymd(first), to: ymd(last) };
    }
    case 'last-quarter': {
      const m = today.getMonth();
      const qStart = Math.floor(m / 3) * 3;
      const lastQEnd = new Date(today.getFullYear(), qStart, 0);
      const lastQStart = new Date(lastQEnd.getFullYear(), lastQEnd.getMonth() - 2, 1);
      return { from: ymd(lastQStart), to: ymd(lastQEnd) };
    }
    case 'ytd': {
      const from = new Date(today.getFullYear(), 0, 1);
      return { from: ymd(from), to: ymd(today) };
    }
    case 'last-year': {
      const y = today.getFullYear() - 1;
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    }
  }
}

export function SalesPerformancePage() {
  const [preset, setPreset] = useState<PresetKey>('30d');
  const [custom, setCustom] = useState(() => presetRange('30d'));
  const range = useMemo(
    () => (preset === 'custom' ? custom : presetRange(preset)),
    [preset, custom],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['salesperson', range.from, range.to],
    queryFn: () =>
      apiFetch<PerfResponse>(
        `/api/dashboard/salesperson-performance?from=${range.from}&to=${range.to}`,
      ),
  });

  const totals = data
    ? data.rows.reduce(
        (acc, r) => ({
          invoiceCount: acc.invoiceCount + r.invoiceCount,
          invoicedRevenue: acc.invoicedRevenue + r.invoicedRevenue,
          arOutstanding: acc.arOutstanding + r.arOutstanding,
          openSoCount: acc.openSoCount + r.openSoCount,
          openSoValue: acc.openSoValue + r.openSoValue,
        }),
        { invoiceCount: 0, invoicedRevenue: 0, arOutstanding: 0, openSoCount: 0, openSoValue: 0 },
      )
    : null;

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Salesperson performance</h1>
        <span className="main-subtitle">
          {data ? `${data.rows.length} active · ${dateOnly(range.from)} – ${dateOnly(range.to)}` : ''}
        </span>
      </header>

      <section className="panel" style={{ padding: '0.75rem 1rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
          {(Object.keys(PRESETS) as PresetKey[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setPreset(k)}
              style={{
                background: preset === k ? 'var(--accent)' : 'var(--surface-2)',
                color: preset === k ? 'var(--bg)' : 'var(--text)',
                border: '1px solid var(--border)',
                padding: '0.4rem 0.75rem',
                cursor: 'pointer',
                fontSize: 12,
                borderRadius: 2,
                fontWeight: preset === k ? 600 : 400,
              }}
            >
              {PRESETS[k]}
            </button>
          ))}
          {preset === 'custom' && (
            <span style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginLeft: '0.5rem' }}>
              <input
                type="date"
                value={custom.from}
                max={custom.to}
                onChange={(e) => setCustom({ ...custom, from: e.target.value })}
                style={{ padding: '0.3rem' }}
              />
              <span style={{ color: 'var(--text-muted)' }}>–</span>
              <input
                type="date"
                value={custom.to}
                min={custom.from}
                onChange={(e) => setCustom({ ...custom, to: e.target.value })}
                style={{ padding: '0.3rem' }}
              />
            </span>
          )}
        </div>
      </section>

      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}

      {data && totals && (
        <>
          <div className="kpi-grid">
            <KPI label="Invoices issued" value={num(totals.invoiceCount)} sub="in range" />
            <KPI label="Revenue" value={money(totals.invoicedRevenue)} sub="in range" />
            <KPI label="Open SO pipeline" value={money(totals.openSoValue)} sub={`${num(totals.openSoCount)} orders`} />
            <KPI label="AR outstanding" value={money(totals.arOutstanding)} sub="all open invoices" />
          </div>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">By salesperson</h2>
              <span className="panel-subtitle">sorted by invoiced revenue</span>
            </header>
            {data.rows.length === 0 ? (
              <div className="empty">No invoiced activity in this range.</div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Salesperson</th>
                    <th className="num">Invoices</th>
                    <th className="num">Revenue</th>
                    <th className="num">Customers</th>
                    <th className="num">Open SOs</th>
                    <th className="num">SO pipeline</th>
                    <th className="num">AR outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.salesman}>
                      <td className="mono">{r.salesman}</td>
                      <td className="num">{num(r.invoiceCount)}</td>
                      <td className="num" style={{ fontWeight: 600 }}>{money(r.invoicedRevenue)}</td>
                      <td className="num">{num(r.uniqueCustomers)}</td>
                      <td className="num">{num(r.openSoCount)}</td>
                      <td className="num">{money(r.openSoValue)}</td>
                      <td className="num" style={{ color: r.arOutstanding > 0 ? 'var(--text)' : 'var(--text-dim)' }}>
                        {money(r.arOutstanding)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </>
  );
}

function KPI({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="kpi-card">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}
